import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { User, MyTenant } from '@niko-crm/ui';
import { UserActions } from './user.actions';

export interface UsersState extends EntityState<User> {
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
  myTenants: MyTenant[];
  myTenantsLoading: boolean;
  myTenantsError: string | null;
}

export const usersAdapter: EntityAdapter<User> = createEntityAdapter<User>();

const initialState: UsersState = usersAdapter.getInitialState({
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
  myTenants: [],
  myTenantsLoading: false,
  myTenantsError: null,
});

export const usersReducer = createReducer(
  initialState,
  on(UserActions.loadUsers, (state) => ({ ...state, loading: true, error: null })),
  on(UserActions.loadUsersSuccess, (state, { result }) =>
    usersAdapter.setAll(result.data, {
      ...state,
      total: result.total,
      page: result.page,
      limit: result.limit,
      loading: false,
    })
  ),
  on(UserActions.loadUsersFailure, (state, { error }) => ({ ...state, loading: false, error })),
  on(UserActions.createUserSuccess, (state, { user }) => usersAdapter.addOne(user, state)),
  on(UserActions.updateUserSuccess, (state, { user }) =>
    usersAdapter.updateOne({ id: user.id, changes: user }, state)
  ),
  // Soft delete (BR-USER-09) — update in place, never `removeOne`. The backend
  // only flips `active` to false; dropping the row made the user vanish from
  // the list until a reload, and would hide the reactivate action entirely.
  on(UserActions.deleteUserSuccess, (state, { user }) =>
    usersAdapter.updateOne({ id: user.id, changes: user }, state)
  ),
  on(UserActions.reactivateUserSuccess, (state, { user }) =>
    usersAdapter.updateOne({ id: user.id, changes: user }, state)
  ),
  on(UserActions.resendInvitationSuccess, (state, { user }) =>
    usersAdapter.updateOne({ id: user.id, changes: user }, state)
  ),
  on(UserActions.resetUserPasswordSuccess, (state, { user }) =>
    usersAdapter.updateOne({ id: user.id, changes: user }, state)
  ),
  on(UserActions.loadMyTenants, (state) => ({
    ...state,
    myTenantsLoading: true,
    myTenantsError: null,
  })),
  on(UserActions.loadMyTenantsSuccess, (state, { tenants }) => ({
    ...state,
    myTenants: tenants,
    myTenantsLoading: false,
  })),
  on(UserActions.loadMyTenantsFailure, (state, { error }) => ({
    ...state,
    myTenantsLoading: false,
    myTenantsError: error,
  })),
);

export const usersFeature = createFeature({ name: 'users', reducer: usersReducer });
